/**
 * Client-side "when does this task next run" — in HER timezone, not the
 * device's and not UTC.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * Task creation in TasksTab built next_run_at with plain setHours(), i.e. the
 * BROWSER's offset:
 *
 *     const next = new Date();
 *     next.setDate(next.getDate() + daysUntil);
 *     next.setHours(hours, minutes, 0, 0);   // <- device timezone, not hers
 *
 * That is correct only while the phone's timezone happens to match the one Kin
 * has stored for her. On a trip, on a desktop set to another zone, or with a
 * clock she never thinks about, the first fire lands at the wrong hour — and
 * for a task whose first fire is also its first text, the wrong hour can be
 * 3 AM. Nothing in the UI would show it: schedule_time still reads "8:00".
 *
 * ── THE EXECUTOR IS THE REFERENCE IMPLEMENTATION ────────────────────────────
 *
 * supabase/functions/run-scheduled-tasks/scheduleTime.ts already solved this
 * in #501, and it owns the after-first-fire schedule. This file is a
 * deliberate port of its primitives — a Deno function module can't be imported
 * into the Vite bundle.
 *
 * ⚠️ CHANGE BOTH TOGETHER. The two must agree about what "Sunday at 08:00"
 * means, or a task gets created for one instant and rescheduled to a different
 * one on its first run — the exact divergence that hid the Aug 3 timing bug.
 *
 * Semantics are matched on purpose, including:
 *   - weekly: ALWAYS advance, so a task never reschedules to this same instant
 *   - interval: N days from today, at schedule_time
 *   - the quiet-hours floor, so a FIRST fire can't land at 3 AM either
 *
 * The timezone COLUMN is user_preferences.notifications_timezone — the same one
 * the executor reads. profiles.timezone also exists; two sources that can
 * disagree is how you get a mom whose briefing is correct and whose reminders
 * are not (CLAUDE.md).
 */

/** No scheduled-task SMS before this hour, local. Mirrors scheduleTime.ts. */
export const QUIET_END_HOUR = 7;
/** No scheduled-task SMS at or after this hour, local. Mirrors scheduleTime.ts. */
export const QUIET_START_HOUR = 21;

/**
 * The hour a scheduled task fires, local to HER zone.
 *
 * ⚠️ #1770 — ONE NUMBER, AND EVERYTHING ELSE IS DERIVED FROM IT.
 *
 * Three call sites in TasksTab built `next_run_at` at 09:00 while writing
 * `schedule_time: "08:00:00"` on the same row. The runner uses `next_run_at`
 * for the FIRST fire and then recomputes from `schedule_time`
 * (`run-scheduled-tasks/index.ts` — `(task.schedule_time || "08:00")`), so the
 * first birthday reminder, friend check-in and weekly nudge landed at 9am and
 * every one after them at 8am. #1748 fixed the identical split on the server,
 * where `REMINDER_HOUR = 9` faced the same `"08:00:00"`.
 *
 * 8 is the value the stored row, the runner's fallback, and the chat path
 * (`kin-chat/birthdayReminderContact.ts`) already agreed on. The 9s were the
 * outliers.
 */
export const DEFAULT_SCHEDULE_HOUR = 8;

/** Fallback when a task has no explicit time. Matches the column default. */
export const DEFAULT_SCHEDULE_TIME = `${String(DEFAULT_SCHEDULE_HOUR).padStart(2, "0")}:00`;

/**
 * The same instant in the "HH:MM:SS" spelling `scheduled_tasks.schedule_time`
 * rows are written with. Derived, so it cannot drift from the hour above —
 * writing `8` and `"08:00:00"` as two literals is what this constant exists to
 * stop.
 */
export const DEFAULT_SCHEDULE_TIME_SQL = `${DEFAULT_SCHEDULE_TIME}:00`;

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

/**
 * How far `tz` is from UTC at a given instant, in ms.
 *
 * Derived from Intl rather than a fixed table so DST is handled by the
 * platform's tz database. Positive east of UTC.
 */
function tzOffsetMs(instant: Date, tz: string): number | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour12: false,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    }).formatToParts(instant).reduce<Record<string, string>>((acc, p) => {
      if (p.type !== "literal") acc[p.type] = p.value;
      return acc;
    }, {});
    const asUtc = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
    );
    if (Number.isNaN(asUtc)) return null;
    return asUtc - instant.getTime();
  } catch {
    return null; // unknown IANA zone
  }
}

/** True when `tz` is a usable IANA zone. */
export function isValidTimeZone(tz: string | null | undefined): boolean {
  if (!tz || typeof tz !== "string") return false;
  return tzOffsetMs(new Date(), tz) !== null;
}

/** Parse "HH:MM" (or "HH:MM:SS"). Null on anything else — never a silent 0. */
export function parseScheduleTime(
  value: string | null | undefined,
): { hours: number; minutes: number } | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value ?? "").trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

/**
 * The UTC instant at which a given wall-clock time occurs in `tz`.
 *
 * Two passes: guess, measure the offset at the guess, correct, then re-measure
 * in case the correction crossed a DST boundary. One pass is wrong for roughly
 * one hour twice a year, which is exactly the kind of bug that reappears at
 * 2 AM in November and nobody can reproduce.
 */
export function zonedWallClockToUtc(
  year: number, month: number, day: number,
  hours: number, minutes: number, tz: string,
): Date | null {
  const guess = Date.UTC(year, month, day, hours, minutes, 0, 0);
  const off1 = tzOffsetMs(new Date(guess), tz);
  if (off1 === null) return null;
  const corrected = guess - off1;
  const off2 = tzOffsetMs(new Date(corrected), tz);
  if (off2 === null) return null;
  return new Date(guess - off2);
}

/** The local wall-clock fields of an instant in `tz`. */
export function localPartsOf(instant: Date, tz: string): {
  year: number; month: number; day: number; hours: number; minutes: number; weekday: number;
} | null {
  const off = tzOffsetMs(instant, tz);
  if (off === null) return null;
  const shifted = new Date(instant.getTime() + off);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
    weekday: shifted.getUTCDay(),
  };
}

/** True when this instant falls in her quiet window [21:00, 07:00). */
export function isQuietHour(instant: Date, tz: string): boolean | null {
  const p = localPartsOf(instant, tz);
  if (!p) return null;
  return p.hours >= QUIET_START_HOUR || p.hours < QUIET_END_HOUR;
}

/**
 * Push an instant forward to the end of quiet hours, if it lands inside them.
 * Returns the input untouched when it is already fine.
 */
export function holdUntilQuietEnd(instant: Date, tz: string): Date | null {
  const quiet = isQuietHour(instant, tz);
  if (quiet === null) return null;
  if (!quiet) return instant;

  const p = localPartsOf(instant, tz);
  if (!p) return null;
  // Before 07:00 -> this morning. At/after 21:00 -> tomorrow morning.
  const dayOffset = p.hours >= QUIET_START_HOUR ? 1 : 0;
  return zonedWallClockToUtc(p.year, p.month, p.day + dayOffset, QUIET_END_HOUR, 0, tz);
}

/** The device's IANA zone, or null if the platform won't say. */
export function deviceTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

export interface ResolvedTimezone {
  timezone: string;
  /**
   * True when this came from the DEVICE, not from her stored preference.
   *
   * A guess must never look like a fact. Callers persist this alongside the
   * row so inferred rows stay queryable and correctable later.
   */
  inferred: boolean;
}

/**
 * Which timezone to schedule in.
 *
 * Her stored notifications_timezone wins. When it is missing or unparseable we
 * do NOT block her and do NOT create the task inactive — both punish her for
 * something she cannot see. We fall back to the device zone, which is right for
 * nearly everyone, and mark the result `inferred` so it can be corrected.
 *
 * Never UTC. Defaulting to UTC is what put reminders at 4 AM in the first place.
 */
export function resolveSchedulingTimezone(
  stored: string | null | undefined,
): ResolvedTimezone | null {
  if (isValidTimeZone(stored)) return { timezone: stored as string, inferred: false };
  const device = deviceTimeZone();
  if (isValidTimeZone(device)) return { timezone: device as string, inferred: true };
  return null;
}

/**
 * A wall-clock hour on a SPECIFIC calendar date, in her zone, as a UTC instant.
 *
 * For the tasks whose first fire is pinned to a date she chose — a birthday, a
 * "remind me on this day" nudge — rather than to an interval from today.
 *
 * ⚠️ The date shift happens in CALENDAR terms before the zone conversion, not
 * as milliseconds after it. Subtracting `leadDays * 86400000` from an instant
 * crosses a DST boundary an hour off, which for a 7-day lead in March or
 * November is exactly the kind of silent hour this whole module exists to
 * prevent.
 *
 * Returns null on an unparseable date or an unusable zone — the caller decides,
 * the same contract as nextRunAtLocal.
 */
export function nextRunAtOnDate(
  ymd: string,
  hour: number,
  timezone: string | null,
  dayOffset = 0,
): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd ?? "").trim());
  if (!m) return null;
  const zone = resolveSchedulingTimezone(timezone);
  if (!zone) return null;
  const y = Number(m[1]), mo = Number(m[2]) - 1, d = Number(m[3]);
  const shifted = new Date(Date.UTC(y, mo, d));
  // ⚠️ Date.UTC NORMALISES an impossible date rather than rejecting it, so
  // "2026-13-45" silently becomes 2027-02-14. Round-trip the parse before
  // trusting it — the same guard createEventBuilder needs for the same reason.
  if (
    shifted.getUTCFullYear() !== y ||
    shifted.getUTCMonth() !== mo ||
    shifted.getUTCDate() !== d
  ) return null;
  shifted.setUTCDate(shifted.getUTCDate() + dayOffset);
  return zonedWallClockToUtc(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth(),
    shifted.getUTCDate(),
    hour,
    0,
    zone.timezone,
  );
}

export interface NextRunInput {
  schedule_day: string | null;
  schedule_time: string | null;
  interval_days: number | null;
  /** IANA zone. Null/invalid -> null, and the caller decides. */
  timezone: string | null;
  /** Injected so tests don't depend on the wall clock. */
  now?: Date;
}

/**
 * When this task should next run, as a UTC instant.
 *
 * Line-for-line the same rules as the executor's calculateNextRunAtLocal.
 * Returns null only when the zone is unusable.
 */
export function nextRunAtLocal(input: NextRunInput): Date | null {
  const tz = input.timezone;
  if (!isValidTimeZone(tz)) return null;
  const zone = tz as string;

  const now = input.now ?? new Date();
  const t = parseScheduleTime(input.schedule_time)
    ?? parseScheduleTime(DEFAULT_SCHEDULE_TIME)!;
  const nowLocal = localPartsOf(now, zone);
  if (!nowLocal) return null;

  const atLocal = (dayDelta: number) =>
    zonedWallClockToUtc(
      nowLocal.year, nowLocal.month, nowLocal.day + dayDelta,
      t.hours, t.minutes, zone,
    );

  let next: Date | null = null;

  if (input.schedule_day) {
    const target = DAY_NAME_TO_INDEX[input.schedule_day.toLowerCase()];
    if (target !== undefined) {
      let daysUntil = target - nowLocal.weekday;
      // Always advance: a task created now must not be scheduled for this instant.
      if (daysUntil <= 0) daysUntil += 7;
      next = atLocal(daysUntil);
    }
  }

  if (!next && input.interval_days && input.interval_days > 0) {
    next = atLocal(input.interval_days);
  }

  if (!next) {
    next = atLocal(7); // same fallback as the executor, in her timezone
  }
  if (!next) return null;

  return holdUntilQuietEnd(next, zone);
}

/**
 * Last resort, reached only if Intl itself cannot name a zone — the task still
 * gets created, using the device offset, which is what we would have inferred
 * anyway. This is the OLD behaviour, confined to the case where the
 * alternative is refusing to save her task.
 */
export function deviceLocalNextRunAt(time: string, dayDelta: number): string {
  const t = parseScheduleTime(time) ?? { hours: 8, minutes: 0 };
  const next = new Date();
  next.setDate(next.getDate() + dayDelta);
  next.setHours(t.hours, t.minutes, 0, 0);
  return next.toISOString();
}

/**
 * `next_run_at` for an interval-cadence row: `intervalDays` out, at `time` in
 * her timezone.
 *
 * #553 — lived in TasksTab as a module-private helper, which is why
 * `createSizeCheckTask` (src/lib) could not reach it and open-coded
 * `Date.now() + intervalDays * 86400000` instead. That expression carries the
 * creation-moment clock time, so a size_check row created at 22:39 fired at
 * 22:39 — and the #501 quiet-hours floor then suppressed the text, leaving her
 * with a card in the app, no text, and no explanation. Every interval creation
 * path now shares this one implementation so they cannot disagree again.
 */
export function nextRunAtByInterval(
  intervalDays: number,
  time: string,
  timezone: string | null,
  now?: Date,
): string {
  const zone = resolveSchedulingTimezone(timezone);
  const next = zone
    ? nextRunAtLocal({
        schedule_day: null,
        schedule_time: time,
        interval_days: intervalDays,
        timezone: zone.timezone,
        now,
      })
    : null;
  if (next) return next.toISOString();
  return deviceLocalNextRunAt(time, intervalDays);
}
